import { useState, useRef, FormEvent, ReactNode } from 'react';
import { ALL_PROVINCES, REGION_HIERARCHY } from '@/mocks/regionData';

const FORM_URL = 'https://readdy.ai/api/form/dalo51k4ns3ag3t2kdpg';

const SHIFT_OPTIONS = ['24시간', '주간', '야간'];
const CERTIFICATE_OPTIONS = ['요양보호사', '간호조무사', '간호사', '사회복지사', '기타', '없음'];
const JOB_TYPE_OPTIONS = ['병원간병', '재택간병', '단기간병', '장기간병', '24시간 간병'];
const PATIENT_GENDER_OPTIONS = ['남성환자', '여성환자'];
const EXPERIENCE_OPTIONS = ['신입', '1년 미만', '1~3년', '3~5년', '5~10년', '10년 이상'];

const STEPS = [
  { id: 1, label: '기본정보', icon: 'ri-user-line' },
  { id: 2, label: '활동정보', icon: 'ri-map-pin-line' },
  { id: 3, label: '경력정보', icon: 'ri-award-line' },
  { id: 4, label: '가능업무', icon: 'ri-briefcase-line' },
] as const;

const YES_NO_FIELDS = [
  { key: 'hospitalExp', label: '병원간병 경험', options: ['있음', '없음'] },
  { key: 'homeExp', label: '재택간병 경험', options: ['있음', '없음'] },
  { key: 'severeExp', label: '중증환자 경험', options: ['있음', '없음'] },
  { key: 'dementiaExp', label: '치매환자 경험', options: ['있음', '없음'] },
  { key: 'diaperCare', label: '기저귀케어 가능 여부', options: ['가능', '불가능'] },
  { key: 'suctionExp', label: '석션 경험', options: ['있음', '없음'] },
] as const;

type YesNoKey = (typeof YES_NO_FIELDS)[number]['key'];

interface CaregiverForm {
  name: string;
  phone: string;
  residenceProvince: string;
  residenceCity: string;
  availableRegions: string[];
  shifts: string[];
  dailyWage: string;
  startDate: string;
  immediate: boolean;
  experience: string;
  certificates: string[];
  hospitalExp: string;
  homeExp: string;
  severeExp: string;
  dementiaExp: string;
  diaperCare: string;
  suctionExp: string;
  jobTypes: string[];
  patientGenders: string[];
  privacyAgree: boolean;
}

const INITIAL_FORM: CaregiverForm = {
  name: '',
  phone: '',
  residenceProvince: '',
  residenceCity: '',
  availableRegions: [],
  shifts: [],
  dailyWage: '',
  startDate: '',
  immediate: false,
  experience: '',
  certificates: [],
  hospitalExp: '',
  homeExp: '',
  severeExp: '',
  dementiaExp: '',
  diaperCare: '',
  suctionExp: '',
  jobTypes: [],
  patientGenders: [],
  privacyAgree: false,
};

function formatPhone(v: string): string {
  const digits = v.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

function isValidPhone(p: string): boolean {
  return /^010-\d{4}-\d{4}$/.test(p);
}

export default function RegistrationForm() {
  const [step, setStep] = useState<number>(1);
  const [form, setForm] = useState<CaregiverForm>(INITIAL_FORM);
  const [errors, setErrors] = useState<string[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const honeypotRef = useRef<HTMLInputElement>(null);
  const sectionRef = useRef<HTMLElement>(null);

  const setField = <K extends keyof CaregiverForm>(key: K, value: CaregiverForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const toggleInArray = (key: 'availableRegions' | 'shifts' | 'jobTypes' | 'patientGenders', value: string) => {
    setForm((prev) => {
      const arr = prev[key];
      return { ...prev, [key]: arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value] };
    });
  };

  const toggleCertificate = (value: string) => {
    setForm((prev) => {
      const arr = prev.certificates;
      if (value === '없음') {
        return { ...prev, certificates: arr.includes('없음') ? [] : ['없음'] };
      }
      const withoutNone = arr.filter((x) => x !== '없음');
      return {
        ...prev,
        certificates: withoutNone.includes(value)
          ? withoutNone.filter((x) => x !== value)
          : [...withoutNone, value],
      };
    });
  };

  const validateStep = (s: number): string[] => {
    const errs: string[] = [];
    if (s === 1) {
      if (!form.name.trim()) errs.push('이름을 입력해 주세요');
      if (!form.phone) errs.push('휴대전화 번호를 입력해 주세요');
      else if (!isValidPhone(form.phone)) errs.push('휴대전화 번호를 010-0000-0000 형식으로 입력해 주세요');
      if (!form.residenceProvince) errs.push('거주지역(시·도)을 선택해 주세요');
      if (!form.residenceCity) errs.push('거주지역(시·군·구)을 선택해 주세요');
    } else if (s === 2) {
      if (form.availableRegions.length === 0) errs.push('활동 가능 지역을 1개 이상 선택해 주세요');
      if (form.shifts.length === 0) errs.push('근무 가능 시간대를 1개 이상 선택해 주세요');
    } else if (s === 3) {
      if (!form.experience) errs.push('간병 경력을 선택해 주세요');
      if (form.certificates.length === 0) errs.push('보유 자격증을 선택해 주세요');
      YES_NO_FIELDS.forEach((f) => {
        if (!form[f.key]) errs.push(`${f.label}을(를) 선택해 주세요`);
      });
    } else if (s === 4) {
      if (form.jobTypes.length === 0) errs.push('가능한 업무 유형을 1개 이상 선택해 주세요');
      if (form.patientGenders.length === 0) errs.push('간병 가능한 환자를 선택해 주세요');
      if (!form.privacyAgree) errs.push('개인정보 수집·이용에 동의해 주세요');
    }
    return errs;
  };

  const goNext = () => {
    const errs = validateStep(step);
    if (errs.length > 0) {
      setErrors(errs);
      return;
    }
    setErrors([]);
    setStep((s) => Math.min(s + 1, 4));
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const goPrev = () => {
    setErrors([]);
    setStep((s) => Math.max(s - 1, 1));
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // 스팸 봇 차단 (honeypot)
    if (honeypotRef.current?.value.trim()) {
      setStatus('success');
      setForm(INITIAL_FORM);
      setStep(1);
      return;
    }

    const errs = validateStep(4);
    if (errs.length > 0) {
      setErrors(errs);
      return;
    }
    setErrors([]);

    const payload = new URLSearchParams();
    payload.append('name', form.name.trim());
    payload.append('phone', form.phone);
    payload.append('residence', `${form.residenceProvince} ${form.residenceCity}`);
    payload.append('availableRegions', form.availableRegions.join(', '));
    payload.append('shifts', form.shifts.join(', '));
    payload.append('dailyWage', form.dailyWage ? `${form.dailyWage}원` : '미정');
    payload.append('startDate', form.immediate ? '즉시 근무 가능' : form.startDate || '미정');
    payload.append('experience', form.experience);
    payload.append('certificates', form.certificates.join(', '));
    YES_NO_FIELDS.forEach((f) => payload.append(f.key, form[f.key]));
    payload.append('jobTypes', form.jobTypes.join(', '));
    payload.append('patientGenders', form.patientGenders.join(', '));
    payload.append('privacyAgree', form.privacyAgree ? '동의' : '미동의');

    setStatus('loading');

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || '';
      const isSuccess = res.ok && parsed?.code === 'OK';

      if (isSuccess) {
        setStatus('success');
        setForm(INITIAL_FORM);
        setStep(1);
      } else {
        setStatus('error');
        setErrorMsg(
          serverMsg && serverMsg.toLowerCase().includes('spam')
            ? '입력 내용을 확인해 주세요.'
            : serverMsg || '제출 중 오류가 발생했습니다.',
        );
      }
    } catch {
      setStatus('error');
      setErrorMsg('네트워크 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    }
  };

  if (status === 'success') {
    return (
      <section id="caregiver-register" className="w-full py-20 bg-white">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div
            className="w-20 h-20 flex items-center justify-center rounded-full mx-auto mb-6"
            style={{ backgroundColor: 'rgba(46,125,107,0.1)' }}
          >
            <i className="ri-checkbox-circle-line text-4xl" style={{ color: '#2E7D6B' }}></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">간병인 사전 등록이 완료되었습니다!</h2>
          <p className="text-sm text-gray-500 leading-relaxed mb-8">
            등록해 주신 정보를 바탕으로, <strong className="text-gray-700">원하는 지역과 근무조건에 맞는 간병 일자리</strong>가
            발생하면 담당자가 안내드립니다.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="/"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#2E7D6B' }}
            >
              <i className="ri-home-4-line"></i>홈으로 돌아가기
            </a>
            <a
              href="tel:15668654"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-bold whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity border"
              style={{ borderColor: '#2E7D6B', color: '#2E7D6B' }}
            >
              <i className="ri-phone-line"></i>전화 상담 1566-8654
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="caregiver-register"
      ref={sectionRef}
      className="w-full py-16 md:py-20 scroll-mt-24"
      style={{ backgroundColor: '#f4faf8' }}
    >
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-8">
          <p className="text-xs font-semibold tracking-widest uppercase mb-2" style={{ color: '#2E7D6B' }}>
            Caregiver Pre-registration
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">간병인 사전 등록</h2>
          <p className="text-xs text-gray-400 mt-2">
            주민등록번호·계좌번호 등 민감정보는 사전 등록 단계에서 수집하지 않습니다.
          </p>
        </div>

        {/* 단계 표시 */}
        <div className="flex items-center justify-center mb-8">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-bold transition-all ${
                    step >= s.id ? 'text-white' : 'text-gray-400 bg-white border border-gray-200'
                  }`}
                  style={step >= s.id ? { backgroundColor: '#2E7D6B' } : undefined}
                >
                  {step > s.id ? <i className="ri-check-line"></i> : <i className={s.icon}></i>}
                </div>
                <span
                  className={`mt-2 text-xs font-medium whitespace-nowrap ${step >= s.id ? 'text-gray-900' : 'text-gray-400'}`}
                >
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className="w-10 sm:w-16 h-px mx-1 sm:mx-2 mb-6"
                  style={{ backgroundColor: step > s.id ? '#2E7D6B' : '#e5e7eb' }}
                ></div>
              )}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} data-readdy-form noValidate>
          {/* 스팸 방지 honeypot */}
          <input
            ref={honeypotRef}
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="contact-pref"
          />

          <div className="rounded-2xl border border-gray-100 p-6 md:p-8 bg-white">
            {/* ───── 1단계: 기본정보 ───── */}
            {step === 1 && (
              <div className="space-y-5">
                <Field label="이름" required>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                    placeholder="성함을 입력해 주세요"
                    className={inputCls}
                  />
                </Field>

                <Field label="휴대전화" required>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={(e) => setField('phone', formatPhone(e.target.value))}
                    placeholder="010-0000-0000"
                    autoComplete="tel"
                    className={inputCls}
                  />
                </Field>

                <Field label="거주지역" required>
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      name="residenceProvince"
                      value={form.residenceProvince}
                      onChange={(e) => {
                        setField('residenceProvince', e.target.value);
                        setField('residenceCity', '');
                      }}
                      className={inputCls}
                    >
                      <option value="">시·도 선택</option>
                      {ALL_PROVINCES.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                    <select
                      name="residenceCity"
                      value={form.residenceCity}
                      onChange={(e) => setField('residenceCity', e.target.value)}
                      className={inputCls}
                      disabled={!form.residenceProvince}
                    >
                      <option value="">시·군·구 선택</option>
                      {(REGION_HIERARCHY[form.residenceProvince] || []).map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </Field>
              </div>
            )}

            {/* ───── 2단계: 활동정보 ───── */}
            {step === 2 && (
              <div className="space-y-6">
                <Field label="활동 가능 지역 (복수 선택)" required>
                  <ChipGroup
                    options={ALL_PROVINCES}
                    selected={form.availableRegions}
                    onToggle={(v) => toggleInArray('availableRegions', v)}
                  />
                </Field>

                <Field label="근무 가능 시간대 (복수 선택)" required>
                  <ChipGroup
                    options={SHIFT_OPTIONS}
                    selected={form.shifts}
                    onToggle={(v) => toggleInArray('shifts', v)}
                    columns={3}
                  />
                </Field>

                <Field label="원하는 일당 (선택)">
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      name="dailyWage"
                      value={form.dailyWage}
                      onChange={(e) => setField('dailyWage', e.target.value.replace(/\D/g, ''))}
                      placeholder="예: 150000"
                      className={`${inputCls} pr-8`}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">원</span>
                  </div>
                </Field>

                <Field label="근무 가능 시작일 (선택)">
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="immediate"
                        checked={form.immediate}
                        onChange={(e) => setField('immediate', e.target.checked)}
                        className="w-4 h-4 cursor-pointer accent-[#2E7D6B]"
                      />
                      <span className="text-sm text-gray-700 font-medium">즉시 근무 가능</span>
                    </label>
                    <input
                      type="date"
                      name="startDate"
                      value={form.startDate}
                      onChange={(e) => setField('startDate', e.target.value)}
                      disabled={form.immediate}
                      className={`${inputCls} disabled:bg-gray-50 disabled:text-gray-400`}
                    />
                  </div>
                </Field>
              </div>
            )}

            {/* ───── 3단계: 경력정보 ───── */}
            {step === 3 && (
              <div className="space-y-6">
                <Field label="간병 경력" required>
                  <ChipGroup
                    options={EXPERIENCE_OPTIONS}
                    selected={form.experience ? [form.experience] : []}
                    onToggle={(v) => setField('experience', form.experience === v ? '' : v)}
                    columns={3}
                  />
                </Field>

                <Field label="보유 자격증 (복수 선택)" required>
                  <ChipGroup
                    options={CERTIFICATE_OPTIONS}
                    selected={form.certificates}
                    onToggle={toggleCertificate}
                  />
                </Field>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
                  {YES_NO_FIELDS.map((f) => (
                    <Field key={f.key} label={f.label} required>
                      <YesNoGroup
                        value={form[f.key]}
                        options={f.options}
                        onChange={(v) => setField(f.key as YesNoKey, v)}
                      />
                    </Field>
                  ))}
                </div>
              </div>
            )}

            {/* ───── 4단계: 가능업무 ───── */}
            {step === 4 && (
              <div className="space-y-6">
                <Field label="가능한 업무 유형 (복수 선택)" required>
                  <ChipGroup
                    options={JOB_TYPE_OPTIONS}
                    selected={form.jobTypes}
                    onToggle={(v) => toggleInArray('jobTypes', v)}
                  />
                </Field>

                <Field label="간병 가능한 환자 (복수 선택)" required>
                  <ChipGroup
                    options={PATIENT_GENDER_OPTIONS}
                    selected={form.patientGenders}
                    onToggle={(v) => toggleInArray('patientGenders', v)}
                    columns={2}
                  />
                </Field>

                <div
                  className="rounded-xl px-5 py-4 text-sm leading-relaxed"
                  style={{ backgroundColor: '#f0f9f6', borderLeft: '4px solid #2E7D6B' }}
                >
                  <p className="text-gray-700">
                    등록해 주신 정보는 <strong>일자리 매칭 안내</strong> 목적으로만 사용됩니다.
                    주민등록번호, 계좌번호 등 민감정보는 실제 계약·정산 시점에 별도로 안내해 드립니다.
                  </p>
                </div>

                <div
                  className="w-full h-40 overflow-y-auto rounded-xl border border-gray-200 p-4 text-xs text-gray-500 leading-relaxed bg-gray-50 whitespace-pre-line"
                >
{`제1조 (개인정보의 처리목적)
회사는 다음의 목적을 위하여 개인정보를 처리합니다.
1. 간병인 사전 등록 및 간병 일자리 매칭 안내
2. 간병 서비스 제공 및 계약·정산 업무 수행

제2조 (수집하는 개인정보 항목)
성명, 휴대전화번호, 거주지역, 활동 가능 지역, 근무 가능 시간대, 간병 경력, 자격증, 가능 업무 유형 등

제3조 (보유 및 이용기간)
정보주체의 동의일로부터 3년간 보유·이용하며, 이후 즉시 파기합니다.`}
                </div>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="privacyAgree"
                    checked={form.privacyAgree}
                    onChange={(e) => setField('privacyAgree', e.target.checked)}
                    className="w-4 h-4 cursor-pointer accent-[#2E7D6B] flex-shrink-0"
                  />
                  <span className="text-sm text-gray-700 font-medium">개인정보 수집 및 이용에 동의합니다.</span>
                </label>
              </div>
            )}

            {/* 에러 메시지 */}
            {errors.length > 0 && (
              <div className="mt-6 rounded-xl px-4 py-3" style={{ backgroundColor: '#fef2f2' }}>
                <ul className="space-y-1">
                  {errors.map((err) => (
                    <li key={err} className="text-xs text-red-500 flex items-center gap-1.5">
                      <i className="ri-error-warning-line text-sm"></i>{err}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {status === 'error' && errorMsg && (
              <p className="mt-4 text-center text-xs text-red-500">{errorMsg}</p>
            )}
          </div>

          {/* 네비게이션 버튼 */}
          <div className="mt-6 flex items-center gap-3">
            {step > 1 && (
              <button
                type="button"
                onClick={goPrev}
                className="flex-1 py-4 rounded-xl text-sm font-bold text-gray-600 whitespace-nowrap cursor-pointer hover:bg-gray-100 transition-colors border border-gray-200 bg-white"
              >
                <i className="ri-arrow-left-line mr-1"></i>이전
              </button>
            )}

            {step < 4 ? (
              <button
                type="button"
                onClick={goNext}
                className="flex-1 py-4 rounded-xl text-base font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#2E7D6B' }}
              >
                다음 단계<i className="ri-arrow-right-line ml-1"></i>
              </button>
            ) : (
              <button
                type="submit"
                disabled={status === 'loading'}
                className="flex-1 py-4 rounded-xl text-base font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-60"
                style={{ backgroundColor: '#2E7D6B' }}
              >
                {status === 'loading' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>등록 중...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-user-add-line"></i>간병인 등록 완료하기
                  </span>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}

/* ─── 공통 스타일 ─── */
const inputCls =
  'w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none transition-all focus:border-[#2E7D6B] bg-white';

/* ─── 필드 래퍼 ─── */
function Field({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-2">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

/* ─── 복수 선택 칩 ─── */
function ChipGroup({
  options,
  selected,
  onToggle,
  columns = 3,
}: {
  options: string[];
  selected: string[];
  onToggle: (v: string) => void;
  columns?: number;
}) {
  const gridCls =
    columns === 2 ? 'grid-cols-2' : columns === 3 ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2 sm:grid-cols-4';
  return (
    <div className={`grid gap-2 ${gridCls}`}>
      {options.map((opt) => {
        const active = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onToggle(opt)}
            className={`px-3 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap cursor-pointer transition-all border ${
              active
                ? 'text-white'
                : 'text-gray-600 border-gray-200 bg-white hover:border-[#2E7D6B]'
            }`}
            style={active ? { backgroundColor: '#2E7D6B', borderColor: '#2E7D6B' } : undefined}
          >
            {active && <i className="ri-check-line mr-1"></i>}
            {opt}
          </button>
        );
      })}
    </div>
  );
}

/* ─── 있음/없음 라디오 ─── */
function YesNoGroup({
  value,
  options,
  onChange,
}: {
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex gap-2">
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`flex-1 px-3 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap cursor-pointer transition-all border ${
              active ? 'text-white' : 'text-gray-600 border-gray-200 bg-white hover:border-[#2E7D6B]'
            }`}
            style={active ? { backgroundColor: '#2E7D6B', borderColor: '#2E7D6B' } : undefined}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}